import axios from 'axios';
import { CoachJoinFormData, isValidEmail } from 'hevy-shared';
import { makeAutoObservable } from 'mobx';
import toast from 'react-hot-toast';
import API from 'utils/API';
import { sendEvent } from 'utils/analyticsEvents';

export class JoinViewModel {
  didSuccessfullySubmit = false;
  coachUsername: string;
  isLoading = true;

  joinMetadata: CoachJoinFormData | undefined = undefined;

  showEmailError = false;
  leadEmail = '';
  onLeadEmailChange = (value: string) => {
    this.showEmailError = false;
    this.leadEmail = value;
  };

  get isEmailValid() {
    return isValidEmail(this.leadEmail);
  }

  leadFullname = '';
  onLeadFullnameChange = (value: string) => {
    this.leadFullname = value;
  };
  leadMessage = '';
  onLeadMessageChange = (value: string) => {
    this.leadMessage = value;
  };

  constructor(
    coachUsername: string,
    isPreview: boolean = false,
    previewImage?: string,
    previewMessage?: string,
    previewCoachName?: string,
    previewCoachProfilePic?: string,
  ) {
    this.coachUsername = coachUsername;
    makeAutoObservable(this);
    if (isPreview) {
      this.joinMetadata = {
        coachUsername: this.coachUsername,
        coachFullname: !!previewCoachName ? previewCoachName : undefined,
        joinImageUrl: !!previewImage ? previewImage : undefined,
        joinMessage: !!previewMessage ? previewMessage : undefined,
        coachProfileImageUrl: !!previewCoachProfilePic ? previewCoachProfilePic : undefined,
      };
      this.isLoading = false;
    } else {
      this.fetchJoinData();
    }
  }

  get coachMessage() {
    return (
      this.joinMetadata?.joinMessage ??
      'Hi there! Use the form below to reach out and start your fitness journey.'
    );
  }

  get coachName() {
    return this.joinMetadata?.coachFullname ?? this.joinMetadata?.coachUsername ?? '';
  }

  hasAcceptedTerms = false;
  onUpdateTerms = (value: boolean) => {
    this.hasAcceptedTerms = value;
  };

  onSendClick = async () => {
    if (!this.isEmailValid) {
      this.showEmailError = true;
      return;
    }
    this.isLoading = true;
    try {
      await API.postCreateCoachLead(
        this.coachUsername,
        this.leadFullname,
        this.leadEmail,
        this.leadMessage,
      );
      sendEvent('coachLeadForm_submitted');
      this.didSuccessfullySubmit = true;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        switch (error.response?.data.error) {
          case 'UserIsAlreadyCoached':
            toast.error(
              `Your email address is already associated with a coach. Please use a different email address.`,
            );
            break;
          case 'UserAlreadyHasACoachInvite':
            toast.error(
              `You already have an invite to be coached. Please check your email for more information.`,
            );
            break;
          case 'DuplicateLead':
            toast.error(
              `You have already sent a request to this coach. Please check your email for more information.`,
            );
            break;
          default:
            toast.error('Error sending: please try again.');
            break;
        }
      } else {
        toast.error('Error sending: please try again.');
      }
    }
    this.isLoading = false;
  };

  get isSendEnabled() {
    return (
      !!this.leadEmail &&
      !!this.leadFullname &&
      !!this.leadMessage &&
      !this.isLoading &&
      this.hasAcceptedTerms
    );
  }

  fetchJoinData = async () => {
    try {
      this.joinMetadata = (await API.getCoachJoinFormMetadata(this.coachUsername)).data;
    } catch (error) {
      toast.error('Unable to load form, please try again later.');
    } finally {
      this.isLoading = false;
    }
  };
}
